import { Button, Divider, Empty, Row, Col, Typography, Table, Tag, Timeline, Dropdown, Menu } from "antd";
import { formatDateFunction, convertSlote } from "../../utils/helper/utils";
import { useDispatch } from "react-redux";
import { changeLoader } from "../../redux/reducers/loader";
import globalRequest from "../../utils/global/globalRequest";
import * as Apis from "../../utils/helper/enum";
import toast from "react-hot-toast";
import { MoreOutlined, UserOutlined, EnvironmentOutlined, CarOutlined, ShopOutlined, ClockCircleOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;

const copyText = async (textToCopy, e) => {
  e.stopPropagation();
  try {
    await navigator.clipboard.writeText(textToCopy);
    toast.success("Copied to clipboard!");
  } catch (err) {
    toast.error("Failed to copy text");
  }
};

const CannedOrderDetails = ({ orderDetailId, orderDetailData, reloadPage }) => {
  const dispatch = useDispatch();

  const zone5Order = orderDetailData?.data; // Regular order details (Branch, Driver, Logs)
  const cannedOrder = orderDetailData?.cannedOrder; // Canned specific details (Machine, Items)

  const handleUpdateStatus = async () => {
    dispatch(changeLoader(true));
    try {
      let response = await globalRequest(
        `${Apis.MANAGE_CANNED_ORDER.UPDATE_STATUS}/${orderDetailId}`,
        "put",
        { status: "delivered" },
        {},
        true
      );
      if (response?.status === 200) {
        toast.success("Order marked as delivered successfully");
        reloadPage();
      } else {
        toast.error(response?.data?.message || "Failed to update status");
      }
    } catch (e) {
      toast.error(e?.message || "Something went wrong");
    }
    dispatch(changeLoader(false));
  };

  if (!orderDetailId || !cannedOrder) {
    return (
      <div className="flex-auto pt-5 min-h-[490px] flex items-center justify-center bg-white border-l border-gray-200">
        <Empty description="Select an order to view details" />
      </div>
    );
  }

  const menu = (
    <Menu>
      <Menu.Item key="1">Print Invoice</Menu.Item>
      <Menu.Item key="2">Cancel Order</Menu.Item>
    </Menu>
  );

  return (
    <div className="flex-auto bg-white border-l border-gray-250 h-[881px] overflow-y-auto pb-10 relative">
      {/* 1. Header Section */}
      <div className="p-5 border-b border-gray-250 sticky top-0 bg-white z-20 shadow-sm">
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Title level={4} className="mb-0 text-gray-800">
                Order#{cannedOrder?.cannedOrderNumber}
              </Title>
              <Text className="text-gray-400 font-normal">({zone5Order?.orderNumber || "N/A"})</Text>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <Tag className="bg-gray-100 border-none text-gray-600 px-3 py-0.5 rounded-sm text-[11px] font-bold uppercase">
                {zone5Order?.deliveryMode || 'Delivery'}
              </Tag>
            </div>
            <Text className="text-[12px] text-gray-500 font-medium">
              {cannedOrder.status === 'delivered' ? 'Delivered on ' : 'Ready to Ship on '}
              <Text className="text-gray-700 font-bold ml-1">
                {formatDateFunction(cannedOrder.updatedAt || cannedOrder.createdAt, "dd/mm/yyyy", true, "at")}
              </Text>
              <Text className="ml-3 text-primary cursor-pointer hover:underline font-bold" onClick={() => document.getElementById('tracking-section').scrollIntoView({ behavior: 'smooth' })}>
                Track History
              </Text>
            </Text>
          </div>
          <div className="flex gap-2">
            <Button className="bg-[#176B53] text-white border-none hover:bg-[#125542] rounded-md px-6">
              Delay Message
            </Button>
            <Dropdown overlay={menu} trigger={['click']}>
              <Button icon={<MoreOutlined />} className="border-none bg-gray-50" />
            </Dropdown>
          </div>
        </div>
      </div>

      {/* 2. Products Tab Section */}
      <div className="px-5 py-4 border-b border-gray-50">
        <div className="flex gap-2 mb-6">
          <Tag className="bg-[#333] text-white px-6 py-1.5 rounded-full border-none cursor-pointer">Products</Tag>
        </div>

        {cannedOrder.items?.map((item, idx) => {
          const itemPinCode = cannedOrder.machineResponse?.itemPinCodes?.find(
            (pin) => pin.itemCode === item.code
          );
          
          return (
            <div key={idx} className="flex justify-between items-center mb-8 pr-10">
              <div className="flex gap-4">
                <div className="w-16 h-16 bg-gray-50 rounded flex items-center justify-center text-2xl">
                  {item.emoji || "📦"}
                </div>
                <div>
                  <Text strong className="text-sm block">{item.name}</Text>
                  <Text type="secondary" className="text-[11px] block mt-1">
                    Pin Code: <Text className="text-gray-700 font-semibold">{itemPinCode?.pinCode || "N/A"}</Text>
                  </Text>
                  <Text type="secondary" className="text-[11px] block mt-1">Images</Text>
                  <Text type="secondary" className="text-[11px] block">Packaging Type : {item.packagingType || "N/A"}</Text>
                  <Text type="secondary" className="text-[11px] block">Item Sensitivity : {item.sensitivity || "N/A"}</Text>
                </div>
              </div>
              <div className="w-16 h-16 bg-gray-100 flex items-center justify-center border border-gray-200">
                <span className="text-[10px] text-gray-400">QR CODE</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* 3. Billing Section */}
      <div className="p-5 border-b border-gray-50">
        <div className="max-w-md ml-auto space-y-2 pr-5">
          <div className="flex justify-between text-sm">
            <Text className="text-gray-500">Item Total</Text>
            <Text strong>SAR {cannedOrder.priceDetails?.productAmount || "0.00"}</Text>
          </div>
          <div className="flex justify-between text-sm">
            <Text className="text-gray-500">Tax (VAT 15%)</Text>
            <Text strong>SAR {cannedOrder.priceDetails?.vatOnProduct || "0.00"}</Text>
          </div>
          <div className="flex justify-between text-sm">
            <Text className="text-gray-500">Delivery Fees</Text>
            <Text strong>SAR {cannedOrder.priceDetails?.deliveryFee || "0.00"}</Text>
          </div>
          <div className="flex justify-between text-sm pt-2">
            <Text strong className="text-gray-700">Bill Total (Amount payable by customer)</Text>
            <Text strong className="text-gray-800">SAR {cannedOrder.priceDetails?.totalAmount || "0.00"}</Text>
          </div>
        </div>
      </div>

      {/* 4. Customer & Delivery Detail Section */}
      <div className="p-5 border-b border-gray-50">
        <Title level={5} className="text-sm font-bold mb-4 text-gray-800">Customer & Delivery Detail</Title>
        <div className="flex gap-4 mb-4 items-start">
          <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-gray-400">
            <UserOutlined />
          </div>
          <div>
            <Text strong className="text-sm">{cannedOrder.customerDetails?.name || "N/A"} | {cannedOrder.customerDetails?.mobile || "N/A"} | {cannedOrder.customerDetails?.email || "N/A"}</Text>
            <div className="mt-2 text-sm">
              <Text type="secondary">Delivery Mode - </Text>
              <Text className="text-[#333] font-medium">{cannedOrder.deliveryDetails?.deliveryMode === 'delivery' ? 'Home' : 'Pickup'}</Text>
            </div>
            <div className="mt-1 text-sm">
              <Text className="text-primary underline cursor-pointer hover:text-primary-dark">
                {cannedOrder.addressDetails?.address || "N/A"}
              </Text>
            </div>
            <div className="mt-2 text-[13px]">
              <Text type="secondary">Delivery Type - </Text>
              <Text className="text-gray-700">{cannedOrder.deliveryDetails?.deliveryType || "Fastest"} {formatDateFunction(cannedOrder.deliveryDetails?.deliveryDate || cannedOrder.createdAt, "dd/mm/yyyy", true, "at")}</Text>
            </div>
            <div className="mt-1 text-[13px]">
              <Text type="secondary">Delivery Pickup Time - </Text>
              <Text className="text-gray-700">{formatDateFunction(zone5Order?.driver_pickup_time || cannedOrder.createdAt, "dd/mm/yyyy", true, "at")}</Text>
            </div>
          </div>
        </div>
      </div>

      {/* 5. Tracking History Section */}
      <div id="tracking-section" className="p-5 border-b border-gray-50">
        <Title level={5} className="text-sm font-bold mb-4 text-gray-800">Tracking History</Title>
        <div className="pl-2">
          {zone5Order?.orderStatusLogs?.length > 0 ? (
            <Timeline className="tracking-timeline">
              {zone5Order.orderStatusLogs.map((log, index) => (
                <Timeline.Item key={index} color="#176B53" dot={<div className="w-2.5 h-2.5 bg-[#176B53] rounded-full" />}>
                  <div className="flex justify-between items-start">
                    <div className="max-w-[70%]">
                      <Text strong className="text-[13px] block">Order {log.status.replace(/_/g, ' ')}</Text>
                      <Text type="secondary" className="text-[11px] block mt-1">
                        Order status has been updated by {log.addedBy?.role || "System"} <Text strong className="text-gray-600">{log.addedBy?.name || "Rabbit Hole"}</Text>
                      </Text>
                      {index === zone5Order.orderStatusLogs.length - 1 && (
                        <Text type="secondary" className="text-[11px] block mt-1">Awaiting for next status...</Text>
                      )}
                    </div>
                    <div className="text-[11px] text-gray-400">
                      {formatDateFunction(log.createdAt, "dd/mm/yyyy", true, "at")}
                    </div>
                  </div>
                </Timeline.Item>
              ))}
            </Timeline>
          ) : (
            <div className="py-4 text-gray-400 italic">Awaiting for tracking updates...</div>
          )}
        </div>
      </div>

      {/* 6. Machine & Branch Details Section */}
      <div className="p-5 border-b border-gray-50">
        <Row gutter={[24, 24]}>
          <Col span={12}>
            <Title level={5} className="text-sm font-bold mb-4 text-gray-800">Vending Machine Information</Title>
            <div className="flex gap-4 items-start">
              <div className="text-gray-400 mt-1">
                <ShopOutlined style={{ fontSize: '20px' }} />
              </div>
              <div>
                <Text strong className="text-sm block">
                  {orderDetailData?.machineDetails?.name || "N/A"}
                </Text>
                <Text className="text-gray-500 text-sm block mt-1">
                  {orderDetailData?.machineDetails?.address || "N/A"}
                </Text>
                <div className="mt-2 flex gap-4">
                  <Text type="secondary" className="text-[11px]">
                    Coordinates: {orderDetailData?.machineDetails?.lat || "0"}, {orderDetailData?.machineDetails?.lng || "0"}
                  </Text>
                  <Tag color={orderDetailData?.machineDetails?.status === 'active' ? 'green' : 'red'} className="text-[10px] uppercase font-bold border-none">
                    {orderDetailData?.machineDetails?.status || "Unknown"}
                  </Tag>
                </div>
              </div>
            </div>
          </Col>
          <Col span={12}>
            <Title level={5} className="text-sm font-bold mb-4 text-gray-800">Branch Detail</Title>
            <div className="flex gap-4 items-start">
              <div className="text-gray-400 mt-1">
                <EnvironmentOutlined style={{ fontSize: '20px' }} />
              </div>
              <div>
                <Text strong className="text-sm block">
                  {zone5Order?.branchDetails?.name || "N/A"} | {zone5Order?.branchDetails?.mobileNo || "+N/A"}
                </Text>
                <Text className="text-primary underline cursor-pointer text-sm block mt-1">
                  {zone5Order?.branchDetails?.address || "N/A"}
                </Text>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* 7. Driver Detail Section */}
      <div className="p-5 border-b border-gray-50">
        <Title level={5} className="text-sm font-bold mb-4 text-gray-800">Driver Detail</Title>
        <div className="flex gap-4 items-start mb-3">
          <div className="w-8 h-8 bg-gray-50 border border-gray-200 rounded flex items-center justify-center text-gray-500">
            <CarOutlined />
          </div>
          <div>
            <Text strong className="text-sm">
              {zone5Order?.driverDetails?.user ? `${zone5Order.driverDetails.user.firstName} ${zone5Order.driverDetails.user.lastName}` : "Not Assigned Yet"} | 
              {zone5Order?.driverDetails?.user?.mobileNo || "N/A"} | 
              Pickup Date & Time : {formatDateFunction(zone5Order?.driver_pickup_time, "dd/mm/yyyy", true, "at")}
            </Text>
          </div>
        </div>
        <Text className="text-primary font-semibold underline cursor-pointer text-[13px] ml-12">Reassign Driver</Text>
      </div>

      {/* 8. Driver Logs Table Section */}
      <div className="p-5">
        <Title level={5} className="text-sm font-bold mb-4 text-gray-800">Driver Logs</Title>
        <div className="overflow-x-auto border border-gray-100 rounded-md">
          <table className="w-full text-left border-collapse min-w-[800px]">
            <thead className="bg-gray-50">
              <tr>
                {/* <th scope="col" className="px-3 py-3 whitespace-nowrap">ID</th> */}
                <th className="p-3 text-[11px] font-bold text-gray-500 border-b">Branch Zone Details</th>
                <th className="p-3 text-[11px] font-bold text-gray-500 border-b">Branch Details</th>
                <th className="p-3 text-[11px] font-bold text-gray-500 border-b">Total Order Volume</th>
                <th className="p-3 text-[11px] font-bold text-gray-500 border-b">Online Drivers</th>
                <th className="p-3 text-[11px] font-bold text-gray-500 border-b">Drivers Based On Vehicle Type</th>
                <th className="p-3 text-[11px] font-bold text-gray-500 border-b">Not Available Drivers</th>
                <th className="p-3 text-[11px] font-bold text-gray-500 border-b">Available Drivers</th>
                <th className="p-3 text-[11px] font-bold text-gray-500 border-b">Nearest Drivers</th>
              </tr>
            </thead>
            <tbody>
              <tr className="hover:bg-gray-50">
                <td className="p-3 text-[12px] text-gray-600 border-b">{zone5Order?.zoneDetails?.[0]?.name || "N/A"}</td>
                <td className="p-3 text-[12px] text-gray-600 border-b">{zone5Order?.branchDetails?.name || "N/A"}</td>
                <td className="p-3 text-[12px] text-gray-600 border-b">1000</td>
                <td className="p-3 text-[12px] text-gray-600 border-b text-primary underline">Sachin Test...</td>
                <td className="p-3 text-[12px] text-gray-600 border-b">-</td>
                <td className="p-3 text-[12px] text-gray-600 border-b text-red-400">No Driver available</td>
                <td className="p-3 text-[12px] text-gray-600 border-b">-</td>
                <td className="p-3 text-[12px] text-gray-600 border-b text-gray-400">No Driver available</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CannedOrderDetails;

